import React, { useEffect, useState } from 'react';
import { useToast } from '../../components/ui/toast/ToastContext';
import { useConfirm } from '../../components/ui/confirm/ConfirmContext';
import { fetchWithCsrf } from '../../utils/fetchWithCsrf';
import MultipleFileInput from '../../components/ui/buttons/MultipleFileInput';
import Button from '../../components/ui/buttons/Button';
import { FileText, X } from 'lucide-react';
import { camelizeKeys } from '../../utils/camelizeKeys';

const Documents = ({ data }) => {
  const [documentsData, setDocumentsData] = useState({ documents: [] });
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const { addToast } = useToast();
  const confirm = useConfirm();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = () => {
    fetch(`/app/client/documents`)
      .then((res) => res.json())
      .then((data) => {
        const camelData = camelizeKeys(data);
        setDocumentsData(camelData);
      });
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (filesToUpload.length === 0) {
      addToast({ type: 'error', message: 'Selecione pelo menos um arquivo.' });
      return;
    }

    const formData = new FormData();
    filesToUpload.forEach((file) => formData.append('files[]', file));

    setIsSaving(true);
    try {
      const res = await fetchWithCsrf(`/app/client/documents`, {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.message || 'Erro ao enviar documentos');
      }

      addToast({ type: 'success', message: 'Documentos enviados com sucesso!' });
      setFilesToUpload([]);
      fetchData();
    } catch (err) {
      addToast({ type: 'error', message: err.message });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (documentId) => {
    const ok = await confirm({
      title: 'CONFIRMAR EXCLUSÃO?',
      description: 'Deseja realmente excluir este documento?',
      confirmText: 'SIM, EXCLUIR',
    });
    if (!ok) return;

    try {
      const res = await fetchWithCsrf(`/app/client/documents/${documentId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Erro ao excluir documento');

      addToast({ type: 'success', message: 'Documento excluído com sucesso!' });
      setDocumentsData((prev) => ({
        ...prev,
        documents: prev.documents.filter((doc) => doc.id !== documentId),
      }));
    } catch (err) {
      addToast({ type: 'error', message: err.message });
    }
  };

  return (
    <div className="flex flex-col gap-5 pb-20">
      {' '}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-5">
        {/* col 1 */}
        <div className="flex flex-col md:col-span-3 gap-5 bg-white rounded-custom20 px-4 py-4 md:px-6">
          <h3 className="font-semibold text-lg md:text-xl text-blueDarker">Documentos enviados</h3>

          <div className="flex flex-col divide-y divide-blueSteel/10">
            {documentsData.documents.map((doc) => (
              <div
                key={doc.id}
                className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 md:gap-6 items-start md:items-center rounded-xl bg-blueLight/50 md:bg-blueLight px-4 py-3 md:px-5 md:py-2"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="rounded-full bg-white p-2 shrink-0">
                    <FileText size={24} className="text-tealLight" strokeWidth={1.5} />
                  </span>
                  <span
                    className="text-blueDarker font-semibold text-sm md:text-base truncate max-w-[70vw] md:max-w-none"
                    title={doc.title}
                  >
                    {doc.title}
                  </span>
                </div>

                <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-10 md:justify-end">
                  <span className="text-blueSteel text-xs md:text-sm">
                    Anexado em{' '}
                    {new Date(doc.createdAt).toLocaleDateString('pt-BR', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                    })}
                  </span>

                  <div className="flex flex-wrap gap-3 md:gap-5">
                    <button
                      className="underline text-tealBase font-medium text-sm md:text-base"
                      onClick={() => window.open(doc.url, '_blank')}
                    >
                      Ver documento
                    </button>
                    <button
                      className="underline text-redSoft font-medium text-sm md:text-base"
                      onClick={() => handleDelete(doc.id)}
                    >
                      Excluir
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {documentsData.documents.length === 0 && (
              <div className="text-blueSteel text-sm py-4 px-2">Nenhum documento enviado ainda.</div>
            )}
          </div>
        </div>

        {/* col 2 */}
        <div className="rounded-custom20 md:col-span-2 bg-white px-4 py-4 md:px-6 flex flex-col gap-5">
          <h3 className="font-semibold text-lg md:text-xl text-blueDarker text-center">Enviar um novo documento</h3>

          <div
            className="flex flex-col items-center justify-center border-dashed border border-tealSteel rounded-lg h-56 sm:h-64 md:h-72 text-blueSteelDark gap-4 md:gap-5 text-center px-4"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const droppedFiles = Array.from(e.dataTransfer.files);
              setFilesToUpload((prev) => [...prev, ...droppedFiles]);
            }}
          >
            <span className="rounded-full bg-blueExtraPale p-6 md:p-8">
              <FileText size={48} strokeWidth={1.5} className="md:hidden" />
              <FileText size={60} strokeWidth={1.5} className="hidden md:block" />
            </span>
            <span className="text-base md:text-lg font-medium">
              Arraste e solte o arquivo ou clique para selecionar
            </span>
            <div className="flex flex-col items-center text-xs md:text-sm leading-snug">
              <span>
                Arquivos suportados: <span>xls, xlsx, pdf, jpg, csv, doc, docx, zip.</span>
              </span>
              <span>
                Tamanho máximo permitido: <span>20 MB</span>
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            {filesToUpload.length > 0 &&
              filesToUpload.map((file, index) => (
                <div key={index} className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText size={16} className="text-tealLight shrink-0" />
                    <span className="text-blueSteel text-xs md:text-sm italic truncate max-w-[70vw] md:max-w-none">
                      {file.name}
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      setFilesToUpload((prev) => prev.filter((_, i) => i !== index));
                    }}
                    className="p-1 rounded hover:bg-red-50"
                    aria-label="Remover arquivo"
                    title="Remover arquivo"
                  >
                    <X size={16} className="text-redSoft" />
                  </button>
                </div>
              ))}
          </div>

          <MultipleFileInput
            onFileSelect={(selectedFiles) => {
              setFilesToUpload((prev) => [...prev, ...selectedFiles]);
            }}
          />

          <div className="flex justify-center">
            <Button
              onClick={handleUpload}
              disabled={isSaving || filesToUpload.length === 0}
              className="w-full sm:w-auto"
            >
              ENVIAR DOCUMENTOS
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Documents;
